import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import PartnerCard from "@/components/mnf/PartnerCard";
import VerseOfWeekCard from "@/components/mnf/VerseOfWeekCard";
import { useStoreQuery, queries } from "@/lib/store";

const mentees = [
  {
    name: "Abigail H.", initials: "AH", since: "2 months in garden",
    quote: "Just completed my first 7-day Move streak! I feel so much lighter in spirit, sister.",
    bloom: "Recent bloom",
  },
  {
    name: "Grace M.",   initials: "GM", since: "5 months in garden",
    quote: "Meditated on Psalm 23 today. Finally found peace with my Nourish goals through His grace.",
    bloom: "Recent bloom",
  },
  {
    name: "Hannah J.",  initials: "HJ", since: "3 weeks in garden",
    quote: "First family meal plan complete. The children even asked for the kale bowl again. Praise God.",
    bloom: "Recent bloom",
  },
];

function AccountabilityEntry() {
  const me = useStoreQuery(() => queries.currentUser());
  const userId = me?.id ?? null;
  const pair = useStoreQuery(() => (userId ? queries.activePairForUser(userId) : null));
  const incoming = useStoreQuery(() => (userId ? queries.pendingInvitesForUser(userId) : []));

  // Active pair or pending invite: show PartnerCard
  if (pair || incoming.length > 0) {
    return <PartnerCard />;
  }
  // Empty: show invite-style entry
  return (
    <Link href="/sisters/partner" className="no-underline block">
      <GlassCard
        organic
        padding={18}
        style={{
          background: "linear-gradient(135deg, rgba(30,74,53,0.10) 0%, rgba(245,240,232,0.85) 100%)",
        }}
      >
        <div className="flex items-center gap-3">
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: "20px 8px 20px 8px",
              background: "var(--mnf-f)",
              color: "#F5F0E8",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon name="diversity_1" size={22} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", fontSize: 10, letterSpacing: "0.16em" }}>
              ACCOUNTABILITY SISTER
            </p>
            <p
              className="mnf-serif"
              style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3, marginTop: 2 }}
            >
              Walk a season with one sister.
            </p>
            <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 2 }}>
              Covenant, check-ins, and quiet whispers between two.
            </p>
          </div>
          <Icon name="chevron_right" size={20} style={{ color: "var(--mnf-tg)" }} />
        </div>
      </GlassCard>
    </Link>
  );
}

export default function Sisters() {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account: VerseOfWeekCard below reads per-user state.
  const userId = me?.id ?? null;

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to see your Sister Circle.
        </p>
      </div>
    );
  }

  return (
    <>
      <ScreenHeader
        eyebrow="Flourishing"
        title="Sister Circle"
        subtitle={
          <>"Like a garden that spans generations, your wisdom is the soil where new sisters take root and bloom."</>
        }
        trailing={
          <button className="flex items-center justify-center hover-elevate" style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(30,74,53,0.08)", color: "var(--mnf-f)" }} aria-label="Notifications">
            <Icon name="notifications" size={18} />
          </button>
        }
      />

      <div className="px-5 pt-1 space-y-4">
        {/* Accountability sister entry */}
        <AccountabilityEntry />

        {/* Sisterhood feed entry */}
        <Link
          href="/sisterhood"
          className="no-underline block"
          data-testid="link-sisterhood-entry"
        >
          <GlassCard
            organic
            padding={16}
            className="mnf-fade-up"
            style={{
              background:
                "linear-gradient(135deg, rgba(127,119,221,0.10) 0%, rgba(30,74,53,0.10) 100%)",
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="shrink-0 inline-flex items-center justify-center"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.7)",
                  fontSize: 22,
                }}
                aria-hidden
              >
                🙏
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="mnf-eyebrow"
                  style={{ color: "var(--mnf-purple)" }}
                >
                  Sisterhood feed
                </p>
                <h3
                  className="mnf-serif"
                  style={{
                    fontSize: 17,
                    fontWeight: 500,
                    color: "var(--mnf-fd)",
                    lineHeight: 1.25,
                  }}
                >
                  Testimonies, prayers, and wins
                </h3>
                <p
                  className="mnf-body mt-1"
                  style={{
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: "var(--mnf-tg)",
                  }}
                >
                  Listen for seven days, then add your voice.
                </p>
              </div>
              <Icon name="chevron_right" size={20} color="var(--mnf-f)" />
            </div>
          </GlassCard>
        </Link>

        {/* Nourish Together challenges entry */}
        <Link
          href="/together"
          className="no-underline block"
          data-testid="link-together-entry"
        >
          <GlassCard
            organic
            padding={16}
            className="mnf-fade-up"
            style={{
              background:
                "linear-gradient(135deg, rgba(192,120,74,0.10) 0%, rgba(160,120,32,0.12) 100%)",
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="shrink-0 inline-flex items-center justify-center"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.7)",
                  fontSize: 22,
                }}
                aria-hidden
              >
                🌿
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="mnf-eyebrow"
                  style={{ color: "var(--mnf-t)" }}
                >
                  Sisterhood circles
                </p>
                <h3
                  className="mnf-serif"
                  style={{
                    fontSize: 17,
                    fontWeight: 500,
                    color: "var(--mnf-fd)",
                    lineHeight: 1.25,
                  }}
                >
                  Nourish Together
                </h3>
                <p
                  className="mnf-body mt-1"
                  style={{
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: "var(--mnf-tg)",
                  }}
                >
                  Threshold, not leaderboard. Walk with us.
                </p>
              </div>
              <Icon name="chevron_right" size={20} color="var(--mnf-f)" />
            </div>
          </GlassCard>
        </Link>

        {/* Verse of the week (compact) */}
        <VerseOfWeekCard userId={userId} variant="compact" />

        {/* Sisters sitting with this verse */}
        <SistersSittingWithVerse />

        {/* Compose wisdom */}
        <GlassCard padding={16} className="mnf-fade-up">
          <div className="flex items-center gap-2 mb-2.5">
            <span style={{ fontSize: 14 }}>📖</span>
            <p className="mnf-body text-xs uppercase" style={{ color: "var(--mnf-f)", fontWeight: 700, letterSpacing: "0.12em" }}>
              Post to wisdom feed
            </p>
          </div>
          <textarea
            placeholder="What spiritual or practical wisdom has the Lord placed on your heart today, beloved sister?"
            rows={3}
            className="w-full mnf-body"
            style={{
              background: "rgba(255,255,255,0.55)",
              border: "1px solid rgba(30,74,53,0.06)",
              borderRadius: 14,
              padding: 12,
              fontSize: 13,
              color: "var(--mnf-tx)",
              resize: "none",
              outline: "none",
            }}
          />
          <div className="flex items-center justify-between mt-2.5">
            <div className="flex gap-1.5">
              <button className="flex items-center justify-center hover-elevate" style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)" }}>
                <Icon name="image" size={16} />
              </button>
              <button className="flex items-center justify-center hover-elevate" style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)" }}>
                <Icon name="auto_awesome" size={16} />
              </button>
            </div>
            <Btn variant="primary" size="sm">Share Wisdom</Btn>
          </div>
        </GlassCard>

        <SectionTitle
          eyebrow="Nurturing seedlings"
          title="Your sisters in growth"
          trailing="3 active mentorships"
        />

        <div className="space-y-3">
          {mentees.map((m, i) => (
            <GlassCard key={i} padding={0} className="mnf-fade-up">
              <div className="px-4 pt-4 pb-2 flex items-center gap-3">
                <Avatar initials={m.initials} size={42} ringColor="rgba(192,120,74,0.5)" />
                <div className="flex-1 min-w-0">
                  <p className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)" }}>
                    {m.name}
                  </p>
                  <p className="text-[11px] mnf-body uppercase" style={{ color: "var(--mnf-t)", letterSpacing: "0.1em", fontWeight: 600 }}>
                    {m.since}
                  </p>
                </div>
                <button className="flex items-center justify-center hover-elevate" style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)" }} aria-label="Message">
                  <Icon name="chat_bubble" size={16} filled />
                </button>
              </div>
              <div
                className="mx-4 mb-2 p-3"
                style={{
                  background: "rgba(192,120,74,0.06)",
                  borderRadius: 12,
                }}
              >
                <p className="mnf-serif italic" style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--mnf-tx)" }}>
                  "{m.quote}"
                </p>
              </div>
              <div className="px-4 pb-3 flex items-center justify-between" style={{ borderTop: "1px dashed rgba(30,74,53,0.10)", paddingTop: 10 }}>
                <span className="text-[11px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.12em", fontWeight: 600 }}>
                  {m.bloom}
                </span>
                <button className="mnf-body" style={{ color: "var(--mnf-f)", fontWeight: 700, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", background: "none", border: "none", cursor: "pointer" }}>
                  Celebrate
                </button>
              </div>
            </GlassCard>
          ))}
        </div>

        {/* Legacy Impact */}
        <GlassCard
          padding={20}
          className="mnf-fade-up relative overflow-hidden"
          style={{
            background: "linear-gradient(165deg, #1E4A35 0%, #163828 100%)",
            color: "#F5F0E8",
            border: "1px solid rgba(245,240,232,0.10)",
          }}
        >
          <div className="mnf-orb" />
          <p className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)" }}>Legacy impact</p>
          <p className="mnf-serif italic mt-1" style={{ fontSize: 14, color: "rgba(245,240,232,0.85)" }}>
            The ripple effect of your grace, beloved.
          </p>

          {/* concentric rings */}
          <div className="my-4 flex items-center justify-center" style={{ height: 110, position: "relative" }}>
            <div className="absolute mnf-pulse-slow" style={{ width: 110, height: 110, borderRadius: "50%", border: "1px solid rgba(245,240,232,0.18)" }} />
            <div className="absolute" style={{ width: 80, height: 80, borderRadius: "50%", border: "1px solid rgba(245,240,232,0.28)" }} />
            <div className="flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(192,120,74,0.30)", border: "1px solid rgba(229,185,151,0.6)" }}>
              <Icon name="diversity_3" size={26} color="#F5F0E8" filled />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <p className="mnf-serif" style={{ fontSize: 28, fontWeight: 500 }}>12</p>
              <p className="text-[10px] mnf-body uppercase" style={{ color: "rgba(245,240,232,0.7)", letterSpacing: "0.16em" }}>
                Sisters mentored
              </p>
            </div>
            <div>
              <p className="mnf-serif" style={{ fontSize: 28, fontWeight: 500 }}>142</p>
              <p className="text-[10px] mnf-body uppercase" style={{ color: "rgba(245,240,232,0.7)", letterSpacing: "0.16em" }}>
                Blooms shared
              </p>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] mnf-body" style={{ color: "rgba(245,240,232,0.85)" }}>Community reached</span>
              <span className="text-[11px] mnf-body" style={{ color: "#F5F0E8", fontWeight: 700 }}>82%</span>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: "rgba(245,240,232,0.15)" }}>
              <div style={{ width: "82%", height: "100%", borderRadius: 999, background: "linear-gradient(90deg, #C0784A, #F5F0E8)" }} />
            </div>
          </div>
        </GlassCard>

        {/* Wisdom quote */}
        <GlassCard
          padding={20}
          className="mnf-fade-up"
          style={{ background: "rgba(255,255,255,0.7)" }}
        >
          <span className="mnf-serif" style={{ fontSize: 48, lineHeight: 0.5, color: "var(--mnf-t)", display: "inline-block" }}>“</span>
          <p className="mnf-serif italic mt-3" style={{ fontSize: 18, lineHeight: 1.45, color: "var(--mnf-fd)" }}>
            "Grace isn't the absence of struggle, but the presence of strength through it."
          </p>
          <p className="text-[10px] mnf-body uppercase mt-3" style={{ color: "var(--mnf-tg)", letterSpacing: "0.16em", fontWeight: 600 }}>
            Shared with the sisterhood yesterday
          </p>
        </GlassCard>

        {/* Quick links */}
        <div className="space-y-2 pb-2">
          {[
            { i: "school",      l: "Sisterhood Masterclasses" },
            { i: "menu_book",   l: "Garden Wisdom Library" },
          ].map((row) => (
            <button
              key={row.l}
              className="w-full flex items-center justify-between mnf-glass hover-elevate"
              style={{ padding: "14px 16px", borderRadius: 16, cursor: "pointer" }}
            >
              <div className="flex items-center gap-3">
                <span
                  className="flex items-center justify-center"
                  style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)" }}
                >
                  <Icon name={row.i} size={18} />
                </span>
                <span className="mnf-body" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--mnf-tx)" }}>
                  {row.l}
                </span>
              </div>
              <Icon name="chevron_right" size={18} color="var(--mnf-tg)" />
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

/* ----------------- Sisters sitting with this week's verse ----------------- */

function SistersSittingWithVerse() {
  const checkIns = useStoreQuery(() => queries.verseCheckInsThisWeek());
  const shared = checkIns.filter((c) => c.sharedToFeed && c.reflection.length > 0);
  if (shared.length === 0) return null;

  return (
    <div data-testid="section-sisters-sitting-with-verse">
      <SectionTitle
        eyebrow="This week"
        title="Sisters sitting with this verse"
        trailing={`${shared.length} reflection${shared.length === 1 ? "" : "s"}`}
      />
      <div className="space-y-2.5 mt-3">
        {shared.slice(0, 4).map((c) => (
          <GlassCard key={c.id} padding={14}>
            <div className="flex items-start gap-3">
              <Avatar initials={c.authorInitials} size={36} ringColor="rgba(160,120,32,0.45)" />
              <div className="flex-1 min-w-0">
                <p
                  className="mnf-serif"
                  style={{ fontSize: 13.5, fontWeight: 500, color: "var(--mnf-fd)" }}
                >
                  {c.authorName}
                </p>
                <p
                  className="mnf-serif italic"
                  style={{
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: "var(--mnf-tx)",
                    marginTop: 4,
                  }}
                >
                  “{c.reflection}”
                </p>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  );
}
